import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Columns3, Loader2, Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { getVzRankSettings, saveVzRankSettings } from "@/lib/verizonSalesApi";

const srcValue = (src) => (src?.type === "ratio" ? `ratio:${src.key}` : `raw:${src?.column || ""}`);
const parseSrc = (v) => (v.startsWith("ratio:") ? { type: "ratio", key: v.slice(6) } : { type: "raw", column: v.slice(4) });

export const SourceSelect = ({ value, options, onChange, testId }) => (
  <select value={srcValue(value)} onChange={(e) => onChange(parseSrc(e.target.value))}
    className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm max-w-[260px]" data-testid={testId}>
    <option value="raw:">Choose data…</option>
    <optgroup label="Ready-made ratios">
      {options.ratios.map((r) => <option key={r.key} value={`ratio:${r.key}`}>{r.label}</option>)}
    </optgroup>
    <optgroup label="DLAR spreadsheet columns">
      {options.columns.map((c) => <option key={c} value={`raw:${c}`}>{c}</option>)}
    </optgroup>
  </select>
);

const ColumnRow = ({ col, i, last, options, onChange, onMove, onRemove }) => (
  <div className="flex items-center gap-2 flex-wrap py-2" data-testid={`vz-lb-col-${i}`}>
    <span className="w-6 text-xs text-slate-400 font-semibold">{i + 1}</span>
    <Input value={col.label} onChange={(e) => onChange({ label: e.target.value })} className="h-9 w-40" placeholder="Heading" data-testid={`vz-lb-col-${i}-label`} />
    <SourceSelect value={col.source} options={options} testId={`vz-lb-col-${i}-source`}
      onChange={(source) => onChange({ source, format: source.type === "ratio" || /%/.test(source.column || "") ? "percent" : col.format })} />
    <select value={col.format} onChange={(e) => onChange({ format: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid={`vz-lb-col-${i}-format`}>
      <option value="number">Number</option><option value="percent">Percent</option>
    </select>
    <Button variant="ghost" size="icon" disabled={i === 0} onClick={() => onMove(-1)} data-testid={`vz-lb-col-${i}-up`}><ArrowUp className="w-4 h-4" /></Button>
    <Button variant="ghost" size="icon" disabled={last} onClick={() => onMove(1)} data-testid={`vz-lb-col-${i}-down`}><ArrowDown className="w-4 h-4" /></Button>
    <Button variant="ghost" size="icon" onClick={onRemove} data-testid={`vz-lb-col-${i}-remove`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
  </div>
);

export const LeaderboardSettingsPanel = ({ onSaved }) => {
  const [data, setData] = useState(null);
  const [cols, setCols] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { getVzRankSettings().then((d) => { setData(d); setCols(d.leaderboard); }).catch(() => toast.error("Failed to load settings")); }, []);

  const save = async (next) => {
    setSaving(true);
    try { const r = await saveVzRankSettings({ leaderboard: next }); setCols(r.leaderboard); toast.success("Leaderboard columns saved"); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const update = (i, patch) => setCols(cols.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const move = (i, d) => { const n = [...cols]; [n[i], n[i + d]] = [n[i + d], n[i]]; setCols(n); };

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;
  return (
    <Card className="border-slate-200" data-testid="vz-lb-settings">
      <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2"><Columns3 className="w-4 h-4 text-teal" /> Leaderboard Columns</CardTitle>
          <p className="text-xs text-slate-500 mt-0.5">#, Store ID and Overall Rank are always shown. Pick any DLAR column — new Verizon columns appear here automatically after the next sync.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => save(data.defaults.leaderboard)} disabled={saving} data-testid="vz-lb-reset-btn"><RotateCcw className="w-4 h-4 mr-1" /> Reset to defaults</Button>
          <Button size="sm" onClick={() => save(cols)} disabled={saving} className="bg-teal hover:bg-teal/90" data-testid="vz-lb-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save columns
          </Button>
        </div>
      </CardHeader>
      <CardContent className="divide-y divide-slate-100">
        {cols.map((c, i) => (
          <ColumnRow key={i} col={c} i={i} last={i === cols.length - 1} options={data.options}
            onChange={(p) => update(i, p)} onMove={(d) => move(i, d)} onRemove={() => setCols(cols.filter((_, j) => j !== i))} />
        ))}
        <div className="pt-3">
          <Button size="sm" variant="outline" onClick={() => setCols([...cols, { label: "", source: { type: "raw", column: "" }, format: "number" }])} data-testid="vz-lb-add-col">
            <Plus className="w-4 h-4 mr-1" /> Add column
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
